import {
	Component,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ViewChild,
	ElementRef,
	AfterViewInit,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
	Optional,
	Self,
	ChangeDetectorRef
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { IdService } from "../../shared/index";
import { makeDateUtils } from "../utils/DateUtils";

@Component({
	selector: "rbc-datepicker-input",
	templateUrl: "./datepicker-input.component.html",
	styles: [],
	encapsulation: ViewEncapsulation.None,
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerInputComponent implements ControlValueAccessor, OnDestroy, OnInit, AfterViewInit {
	@Input() id: string;
	@Input() selectedDate: Date;
	@Input() format: string | Function;
	@Input() useUtc: boolean;
	@Input() translation: Object;
	@Input() value;
	@Input() typeable = true;
	@Input() bootstrapStyling: boolean;
	@Input() inputClass;
	@Input() required = false;
	@Input() invalid;
	@Input() showRequired: boolean;
	@Input() placeholder = "";
	@Input() resetTypedDate: Date;
	@Input() disabled: boolean;
	@Input() errorLabel: string;
	@Input() helpText = "";
	@Input() inline: boolean;
	@Input() label = "";
	@Input() name = "";
	@Input() clearButton: boolean;
	@Input() clearButtonIcon: string;

	@Input() ariaExpanded = false;
	@Input() ariaLabel: string;
	@Input() ariaLabelledby: string;
	@Input() ariaDescribedby: string;

	// @Output() inputTextUpdate = new EventEmitter<any>();
	@Output() clear = new EventEmitter<string>();
	@Output() typedDateEmit = new EventEmitter();
	@Output() closeCalendarEmit = new EventEmitter();
	@Output() onFocus = new EventEmitter();
	@Output() onFocusOut = new EventEmitter();
	@Output() showCalendarEmit = new EventEmitter();
	@Output() inputTextUpdate = new EventEmitter();


	@ViewChild("control", { static: false }) control: ElementRef;

	errorEnabled = false;
	typedDate;
	isMobile = false;
	constructedDateUtils = makeDateUtils(this.useUtc);
	utils = this.constructedDateUtils;
	input = null;
	accessedByClick = false;
	ariaExplanationEnabled = false;
	defaultError = "Please pick a valid date";

	constructor(private idService: IdService, @Optional() @Self() public controlDirective: NgControl, private cdr:ChangeDetectorRef) {
		this.id = this.id ? this.id : this.idService.generate();
		if(this.controlDirective){
			controlDirective.valueAccessor = this;
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnInit() {
		if (this.inputValue === undefined) {
			this.value = "";
		}
	}

	ngAfterViewInit() {
		this.input = this.control.nativeElement;
		this.typedDate = false;
	}

	// Function to call when the rating changes.
	onChange = (rating: number) => { };
	// Function to call when the input is touched (when a star is clicked).
	onTouched = () => { };
	get inputValue() {
		return this.formattedValue();
	}

	set inputValue(val) {
		this.value = val;
	}

	showCalendar(forceShow = false) {
		if (forceShow) {
			this.accessedByClick = true;
		}
		if (!this.isMobile) {
			this.showCalendarEmit.emit({ click: forceShow });
		}
	}

	isEmpty() {
		return this.selectedDate == null;
	}

	error() {
		if (this.required && this.isEmpty() && this.errorEnabled) {
			return true;
		} else {
			return false;
		}
	}

	computedInputClass() {
		if (this.bootstrapStyling) {
			if (typeof this.inputClass === "string") {
				return [this.inputClass, "form-control"].join(" ");
			}
			return { "form-control": true, ...this.inputClass };
		}
		return this.inputClass;
	}

	formattedValue() {
		if (!this.selectedDate) {
			return null;
		}
		if (this.typedDate) {
			return this.typedDate;
		}
		return typeof this.format === "function"
			? this.format(this.selectedDate)
			: this.isMobile
				? this.utils.formatDate(
					new Date(this.selectedDate),
					"yyyy-MM-dd",
					this.translation
				)
				: this.utils.formatDate(
					new Date(this.selectedDate),
					this.format,
					this.translation
				);
	}

	parseTypedDate(event) {
		// close calendar if escape or enter are pressed
		if (
			[
				27, // escape
				13 // enter
			].includes(event.keyCode)
		) {
			this.typedDate = null;
			this.input.blur();
			this.cdr.detectChanges();
			return;
		}

		if (this.typeable) {
			const typedDate = Date.parse(this.input.value);
			if (!isNaN(typedDate)) {
				this.typedDate = this.input.value;
				this.typedDateEmit.emit(new Date(this.typedDate));
			}
			this.inputTextUpdate.emit(this.input.value);
		}
	}

	inputBlurred() {
		this.errorEnabled = true;
		if (!this.accessedByClick) {
			this.ariaExplanationEnabled = !this.ariaExplanationEnabled;
			if (!this.typeable) {
				this.closeCalendarEmit.emit();
			}
			this.accessedByClick = false;
		}

		if (this.typeable) {
			if(isNaN(Date.parse(this.input.value))) {
				this.clear.emit();
			}
			this.typedDate = null;
			this.closeCalendarEmit.emit();
		}
	}


	/**
     * nullify the typed date to defer to regular formatting
     * called once the input is blurred
     */
	// 	inputBlurred () {
	// 	this.errorEnabled = true;
	// 	if (!this.accessedByClick) {
	// 		this.ariaExplanationEnabled = !this.ariaExplanationEnabled;
	// 		if (!this.typeable) {
	// 			this.closeCalendarEmit.emit();
	// 		}
	// 		this.accessedByClick = false;
	// 	}

	// 	if (this.typeable) {
	// 		if (isNaN(Date.parse(this.input.value))) {
	// 			this.clearDate();
	// 			this.input.value = null;
	// 		}
	// 		this.typedDate = null;
	// 		this.closeCalendarEmit.emit();
	// 	}
	// }

	/**
	 * emit a clearDate event
	 */
	clearDate(e?) {
		if (typeof e !== "undefined" && (e.type.indexOf("click") !== -1 || e.key.toLowerCase().indexOf("space") !== -1 || e.key.toLowerCase().indexOf("enter") !== -1)
		) {
			this.clear.emit();
		}
	}

	// Allows Angular to update the model (rating).
	// Update the model and changes needed for the view here.
	writeValue(rating): void {
		this.inputValue = rating;
		this.cdr.detectChanges();
		// this.onChange(this.inputValue);
	}
	// Allows Angular to register a function to call when the model (rating) changes.
	// Save the function as a property to call later here.
	registerOnChange(fn: (rating: number) => void): void {
		this.onChange = fn;
	}
	// Allows Angular to register a function to call when the input has been touched.
	// Save the function as a property to call later here.
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}
	// Allows Angular to disable the input.
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	clearInput() {
		this.writeValue("");
		this.clear.emit("");
	}

	inputFocused(event){
		if (!this.isMobile) {
			this.ariaExplanationEnabled = true;
		}
		this.onFocus.emit(event.target.value);
	}

	inputFocusOut(event){
		this.onFocusOut.emit(event);
	}
}
